import type { QuarantineRecordType, QuarantinedRecord } from '@texas42/app-model';
import type { Texas42Database } from './db.js';
import { STORE_META } from './constants.js';
import { computeChecksum } from './checksum.js';

const QUARANTINE_PREFIX = 'quarantine:';

function quarantineKey(recordType: QuarantineRecordType, id: string): string {
  return `${QUARANTINE_PREFIX}${recordType}:${id}`;
}

export async function quarantineRecord(
  db: Texas42Database,
  recordType: QuarantineRecordType,
  id: string,
  reason: string,
  payload: unknown,
): Promise<QuarantinedRecord> {
  const entry: QuarantinedRecord = {
    id,
    recordType,
    reason,
    detectedAt: new Date().toISOString(),
    payload,
  };

  await db.put(STORE_META, {
    key: quarantineKey(recordType, id),
    value: entry,
    checksum: await computeChecksum(entry),
  });

  return entry;
}

export async function listQuarantinedRecords(db: Texas42Database): Promise<QuarantinedRecord[]> {
  const records: QuarantinedRecord[] = [];
  const all = await db.getAll(STORE_META);
  for (const row of all) {
    if (typeof row.key === 'string' && row.key.startsWith(QUARANTINE_PREFIX)) {
      const value = row.value as QuarantinedRecord;
      records.push(value);
    }
  }
  return records.sort((a, b) => a.detectedAt.localeCompare(b.detectedAt));
}

export async function deleteQuarantinedRecord(
  db: Texas42Database,
  recordType: QuarantineRecordType,
  id: string,
): Promise<void> {
  await db.delete(STORE_META, quarantineKey(recordType, id));
}

export async function clearQuarantine(db: Texas42Database): Promise<void> {
  const keys = (await db.getAllKeys(STORE_META)).filter(
    (key) => typeof key === 'string' && key.startsWith(QUARANTINE_PREFIX),
  );
  await Promise.all(keys.map((key) => db.delete(STORE_META, key)));
}
